import { consoleRoutes } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { AppLink, Avatar, Badge, Button, EmptyState, Icon, TableRowLink } from "@sagot/ui";
import { useNavigate } from "react-router";
import { useWorkspaceContext } from "../../../session/workspace-context";
import type { DashboardOut } from "../shared/api-types";
import type { Formatters } from "../shared/format";
import { CHANNEL_ICONS, STATUS_TONES } from "./dashboard-model";

type Recent = DashboardOut["recent_conversations"][number];

function ConversationRow({ row, format }: { row: Recent; format: Formatters }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { summary } = useWorkspaceContext();
  const href = consoleRoutes.inbox(summary.slug, row.id);
  const status = STATUS_TONES[row.status] ?? { tone: "neutral" as const, icon: "circle" as const };
  return (
    <TableRowLink onNavigate={() => void navigate(href)}>
      <td>
        <div className="flex items-center gap-2.5">
          <Avatar name={row.visitor_name} />
          <AppLink href={href} className="font-semibold hover:underline">
            {row.visitor_name}
          </AppLink>
        </div>
      </td>
      <td>
        <Badge>
          <Icon name={CHANNEL_ICONS[row.channel] ?? "message-square"} className="size-3" />
          {t(`dashboard.channels.${row.channel}`, { defaultValue: row.channel })}
        </Badge>
      </td>
      <td className="max-w-64 truncate text-fg-muted">{row.first_question ?? t("dashboard.recent.noQuestion")}</td>
      <td>
        <Badge tone={status.tone}>
          <Icon name={status.icon} className="size-3" />
          {t(`dashboard.recent.status.${row.status}`, { defaultValue: row.status })}
        </Badge>
      </td>
      <td className="whitespace-nowrap text-right text-fg-subtle">
        <time dateTime={row.last_at ?? undefined} title={format.dateTime(row.last_at)}>
          {format.age(row.last_at)}
        </time>
      </td>
    </TableRowLink>
  );
}

function RecentConversationsCard({ data, format }: { data: DashboardOut; format: Formatters }) {
  const { t } = useTranslation();
  const { summary } = useWorkspaceContext();
  return (
    <div className="card overflow-hidden xl:col-span-2">
      <div className="card-header">
        <h2 className="card-title">{t("dashboard.recent.title")}</h2>
        <Button asChild variant="ghost" size="sm">
          <AppLink href={consoleRoutes.inbox(summary.slug)}>
            {t("dashboard.recent.openInbox")}
            <Icon name="arrow-right" />
          </AppLink>
        </Button>
      </div>
      {data.recent_conversations.length === 0 ? (
        <EmptyState icon="messages-square" headingLevel={3} title={t("dashboard.recent.emptyTitle")} description={t("dashboard.recent.emptyBody")} />
      ) : (
        <div className="overflow-x-auto">
          <table className="table">
            <thead>
              <tr>
                <th>{t("dashboard.recent.colVisitor")}</th>
                <th>{t("dashboard.recent.colChannel")}</th>
                <th>{t("dashboard.recent.colQuestion")}</th>
                <th>{t("dashboard.recent.colStatus")}</th>
                <th className="text-right">{t("dashboard.recent.colWhen")}</th>
              </tr>
            </thead>
            <tbody>
              {data.recent_conversations.map((row) => (
                <ConversationRow key={row.id} row={row} format={format} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function GapsCard({ data }: { data: DashboardOut }) {
  const { t } = useTranslation();
  const { summary } = useWorkspaceContext();
  const gaps = consoleRoutes.gaps(summary.slug);
  return (
    <div className="card">
      <div className="card-header">
        <h2 className="card-title flex items-center gap-2">
          <Icon name="help-circle" className="size-4 text-accent" />
          {t("dashboard.gaps.title")}
        </h2>
        {data.top_gaps.open_count > 0 ? (
          <AppLink href={gaps} className="text-xs font-semibold text-primary-ink hover:underline">
            {t("dashboard.gaps.seeAll", { count: data.top_gaps.open_count })}
          </AppLink>
        ) : null}
      </div>
      {data.top_gaps.items.length === 0 ? (
        <EmptyState icon="check-circle" headingLevel={3} title={t("dashboard.gaps.emptyTitle")} description={t("dashboard.gaps.emptyBody")} className="py-6" />
      ) : (
        <ul className="divide-y divide-line">
          {data.top_gaps.items.map((gap) => (
            <li key={gap.id} className="flex items-center gap-3 px-5 py-3">
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-semibold">{gap.question}</div>
                <div className="text-xs text-fg-subtle">
                  {t("dashboard.gaps.asked", { count: gap.ask_count })}
                  {gap.reason ? ` · ${t(`dashboard.gaps.reasons.${gap.reason}`, { defaultValue: gap.reason })}` : ""}
                </div>
              </div>
              <Button asChild variant="secondary" size="sm">
                <AppLink href={`${gaps}?gap=${encodeURIComponent(gap.id)}`} aria-label={t("dashboard.gaps.answerLabel", { question: gap.question })}>
                  {t("dashboard.gaps.answer")}
                </AppLink>
              </Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function HealthStat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md bg-surface-2 py-3">
      <dt className="text-[11px] font-semibold text-fg-subtle">{label}</dt>
      <dd className="text-lg font-extrabold">{value}</dd>
    </div>
  );
}

function KnowledgeHealthCard({ data, format, agentId }: { data: DashboardOut; format: Formatters; agentId: string | null }) {
  const { t } = useTranslation();
  const { summary } = useWorkspaceContext();
  const kb = data.knowledge;
  const manage = agentId ? consoleRoutes.knowledge(summary.slug, agentId) : consoleRoutes.agents(summary.slug);
  return (
    <div className="card card-body">
      <div className="flex items-center justify-between">
        <h2 className="card-title">{t("dashboard.knowledge.title")}</h2>
        <AppLink href={manage} className="text-xs font-semibold text-primary-ink hover:underline">
          {t("dashboard.knowledge.manage")}
        </AppLink>
      </div>
      {kb.source_count === 0 ? (
        <EmptyState
          icon="book-open"
          headingLevel={3}
          title={t("dashboard.knowledge.emptyTitle")}
          description={t("dashboard.knowledge.emptyBody")}
          className="py-6"
          action={
            <Button asChild variant="secondary" size="sm">
              <AppLink href={manage}>{t("dashboard.knowledge.addSources")}</AppLink>
            </Button>
          }
        />
      ) : (
        <dl className="mt-4 grid grid-cols-3 gap-3 text-center">
          <HealthStat label={t("dashboard.knowledge.sources")} value={format.number(kb.source_count)} />
          <HealthStat label={t("dashboard.knowledge.chunks")} value={format.number(kb.chunk_count)} />
          <HealthStat label={t("dashboard.knowledge.lastSync")} value={kb.last_sync_at ? format.age(kb.last_sync_at) : t("dashboard.knowledge.never")} />
        </dl>
      )}
      {kb.flagged_source ? (
        <AppLink href={consoleRoutes.sourceDetail(summary.slug, kb.flagged_source.id)} className="mt-3 flex items-start gap-2.5 rounded-md border border-line bg-warning-soft px-3 py-2.5 hover:border-accent">
          <Icon name="alert-triangle" className="mt-0.5 size-4 text-warning" />
          <span className="text-xs text-fg">
            <strong>{kb.flagged_source.title}</strong> {t("dashboard.knowledge.flaggedBody", { reason: kb.flagged_source.reason ?? t("dashboard.knowledge.flaggedFallback") })}
          </span>
        </AppLink>
      ) : null}
    </div>
  );
}

/** Recent conversations table (xl:col-span-2) + top gaps and knowledge health. */
export function RecentCards({ data, format, agentId }: { data: DashboardOut; format: Formatters; agentId: string | null }) {
  return (
    <section className="grid grid-cols-1 gap-6 xl:grid-cols-3">
      <RecentConversationsCard data={data} format={format} />
      <div className="space-y-6">
        <GapsCard data={data} />
        <KnowledgeHealthCard data={data} format={format} agentId={agentId} />
      </div>
    </section>
  );
}
